import { useEffect, useRef } from "react";
import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import {
  InteractionRequiredAuthError,
  InteractionStatus
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import * as Sentry from "@sentry/react";

import {
  login,
  setAzureToken,
  setToken
} from "@/redux/authorization/authorization.slice";
import { checkEntraAuthGate } from "@/utils/statsig";
import { debug } from "@/utils/debug";
import { resetState } from "@/utils/redux";
import { userApi } from "@/redux/user/user.api";
import { useAppDispatch } from "@/redux";
import { msalConfig, scopes } from "./authConfig";
import { persistActiveAccountId } from "./msal-instance";
import { clearEntraLoginHint, setEntraLoginHint } from "./login-hint";

interface MsalErrorLike {
  errorCode?: string;
  errorMessage?: string;
  subError?: string;
  correlationId?: string;
  name?: string;
}

const extractMsalErrorDetails = (error: unknown) => {
  const e = error as MsalErrorLike;
  return {
    errorCode: e?.errorCode,
    errorMessage: e?.errorMessage,
    subError: e?.subError,
    correlationId: e?.correlationId,
    name: e?.name
  };
};

const isRecoverableByRedirect = (error: unknown): boolean => {
  if (error instanceof InteractionRequiredAuthError) return true;
  const errorCode = (error as MsalErrorLike)?.errorCode;
  return errorCode === "timed_out" || errorCode === "monitor_window_timeout";
};

const REFRESH_SKEW_MS = 2 * 60 * 1000;
const FALLBACK_REFRESH_MS = 30 * 60 * 1000;

const computeRefreshDelayMs = (expiresOn?: Date | null): number => {
  const expiresAt = expiresOn?.getTime();
  const now = Date.now();
  return typeof expiresAt === "number"
    ? Math.max(5_000, expiresAt - now - REFRESH_SKEW_MS)
    : FALLBACK_REFRESH_MS;
};

/**
 * Keeps Redux auth state in sync with MSAL for Entra: silent acquire + scheduled refresh.
 * Mounted in RootLayout. Does not initiate login — that's the LoginEntry's responsibility.
 * Mirrors NM's `EntraAuthSync`.
 */
export const EntraAuthSync = () => {
  const isUsingEntra = checkEntraAuthGate();
  const { instance, inProgress } = useMsal();
  const dispatch = useAppDispatch();
  const refreshTimeoutIdRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  useEffect(() => {
    if (!isUsingEntra) {
      debug.log("[entra-sync] Entra gate OFF — skipping");
      return;
    }
    if (inProgress !== InteractionStatus.None) {
      debug.log("[entra-sync] MSAL interaction in progress:", inProgress);
      return;
    }

    const account = instance.getActiveAccount();
    if (!account) {
      debug.log("[entra-sync] No active MSAL account — skipping refresh loop");
      return;
    }

    debug.log("[entra-sync] Starting refresh loop for", account.username);

    const effectRun = { cancelled: false };

    const clearRefreshTimer = () => {
      if (refreshTimeoutIdRef.current === null) return;
      globalThis.clearTimeout(refreshTimeoutIdRef.current);
      refreshTimeoutIdRef.current = null;
    };

    const scheduleRefresh = (expiresOn?: Date | null) => {
      clearRefreshTimer();
      const delayMs = computeRefreshDelayMs(expiresOn);
      debug.log("[entra-sync] Token refresh scheduled", {
        expiresOn: expiresOn?.toISOString() ?? "unknown",
        refreshInMs: delayMs,
        refreshInMin: Math.round(delayMs / 60_000)
      });
      refreshTimeoutIdRef.current = globalThis.setTimeout(() => {
        void refreshAccessToken();
      }, delayMs);
    };

    const clearStaleMsalSession = async (
      trigger: string,
      sessionAccount: AccountInfo
    ) => {
      debug.warn("[entra-sync] clearStaleMsalSession called", {
        trigger,
        activeAccount: instance.getActiveAccount()?.username ?? null,
        totalAccounts: instance.getAllAccounts().length
      });
      if (!import.meta.env.DEV) {
        Sentry.captureMessage(
          "MSAL session cleared — user will be logged out",
          {
            level: "warning",
            tags: { authPhase: "clearStaleMsalSession", trigger },
            extra: {
              activeAccount: instance.getActiveAccount()?.username ?? null,
              totalAccounts: instance.getAllAccounts().length
            }
          }
        );
      }

      dispatch(resetState());
      dispatch(userApi.util.resetApiState());
      clearEntraLoginHint();
      instance.setActiveAccount(null);
      try {
        await instance.clearCache({ account: sessionAccount });
      } catch (clearError) {
        debug.error(
          "[entra-sync] clearCache after silent token failure failed:",
          clearError
        );
        void instance.logoutRedirect({
          account: sessionAccount,
          postLogoutRedirectUri:
            msalConfig.auth.postLogoutRedirectUri ?? msalConfig.auth.redirectUri
        });
      }
    };

    const applySilentSuccess = (
      result: AuthenticationResult,
      sessionAccount: AccountInfo
    ) => {
      if (effectRun.cancelled) return;

      const accessToken = result.accessToken;
      if (!accessToken) {
        debug.warn(
          "[entra-sync] acquireTokenSilent returned empty accessToken"
        );
        return;
      }

      const currentUsername = sessionAccount.username;

      dispatch(setToken(accessToken));
      dispatch(setAzureToken(accessToken));
      dispatch(login());
      setEntraLoginHint(currentUsername);
      persistActiveAccountId(sessionAccount.homeAccountId);

      scheduleRefresh(result.expiresOn);
    };

    const refreshAccessToken = async () => {
      if (effectRun.cancelled) return;

      const active = instance.getActiveAccount();
      if (!active) {
        debug.log("[entra-sync] No active account during refresh — aborting");
        return;
      }

      debug.log("[entra-sync] Acquiring token silently...");
      try {
        const result = await instance.acquireTokenSilent({
          scopes,
          account: active
        });
        applySilentSuccess(result, active);
      } catch (error) {
        if (effectRun.cancelled) return;

        const details = extractMsalErrorDetails(error);
        debug.error("[entra-sync] acquireTokenSilent failed:", details);

        if (isRecoverableByRedirect(error)) {
          debug.log(
            "[entra-sync] Recoverable error — redirecting to acquire token",
            { errorCode: details.errorCode }
          );
          if (!import.meta.env.DEV) {
            Sentry.addBreadcrumb({
              category: "msal",
              message: `Recoverable auth error — redirecting (${details.errorCode})`,
              level: "warning",
              data: details
            });
          }
          if (effectRun.cancelled) return;
          await instance.acquireTokenRedirect({
            scopes,
            account: active,
            loginHint: active.username
          });
          return;
        }

        debug.error("[entra-sync] Unrecoverable silent token error:", details);
        if (!import.meta.env.DEV) {
          Sentry.captureException(error, {
            tags: {
              authPhase: "acquireTokenSilent",
              msalErrorCode: details.errorCode ?? "unknown"
            },
            extra: details
          });
        }
        if (!effectRun.cancelled) {
          await clearStaleMsalSession(
            `unrecoverable_error:${details.errorCode ?? "unknown"}`,
            active
          );
        }
      }
    };

    void refreshAccessToken();

    return () => {
      debug.log("[entra-sync] Effect cleanup — cancelling refresh loop");
      effectRun.cancelled = true;
      clearRefreshTimer();
    };
  }, [dispatch, inProgress, instance, isUsingEntra]);

  return null;
};
